import { useEffect, useState } from "react";
import api, { fmtErr } from "../../lib/api";
import { MODULE_LABELS } from "../../components/shared";
import { toast } from "sonner";

const inputCls = "mt-1 w-full bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-900 outline-none";

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [tab, setTab] = useState("pricing");

  useEffect(() => { api.get("/admin/settings").then((r) => setSettings(r.data)).catch((e) => toast.error(fmtErr(e))); }, []);

  const save = async (payload) => {
    try {
      const { data } = await api.put("/admin/settings", payload);
      setSettings(data);
      toast.success("Settings saved");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (!settings) return <p className="text-neutral-500">Loading...</p>;

  const setPlan = (i, k, v) => setSettings((s) => {
    const plans = [...s.pricing_plans];
    plans[i] = { ...plans[i], [k]: v };
    return { ...s, pricing_plans: plans };
  });
  const setSplitT = (i, k, v) => setSettings((s) => {
    const t = [...s.royalty_split_templates];
    t[i] = { ...t[i], [k]: v };
    return { ...s, royalty_split_templates: t };
  });
  const setKey = (k, v) => setSettings((s) => ({ ...s, api_keys: { ...s.api_keys, [k]: v } }));

  return (
    <div data-testid="settings-page" className="max-w-4xl">
      <h1 className="text-3xl font-bold tracking-tight text-neutral-900 font-heading mb-8">Platform Settings</h1>
      <div className="flex gap-2 mb-6">
        {[["pricing", "Pricing Plans"], ["splits", "Royalty Splits"], ["keys", "API Keys"]].map(([k, label]) => (
          <button key={k} data-testid={`settings-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium border ${tab === k ? "bg-neutral-900 text-white border-neutral-900" : "border-neutral-300 text-neutral-600 hover:bg-neutral-50"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "pricing" && (
        <div className="space-y-4">
          {settings.pricing_plans.map((p, i) => (
            <div key={p.id} className="bg-white border border-neutral-200 rounded-lg p-6">
              <div className="grid sm:grid-cols-4 gap-4">
                <div><label className="text-sm font-medium text-neutral-700">Name</label>
                  <input data-testid={`plan-name-${p.id}`} value={p.name} onChange={(e) => setPlan(i, "name", e.target.value)} className={inputCls} /></div>
                <div><label className="text-sm font-medium text-neutral-700">One-time price (USD)</label>
                  <input data-testid={`plan-price-${p.id}`} type="number" step="0.01" value={p.price} onChange={(e) => setPlan(i, "price", parseFloat(e.target.value) || 0)} className={inputCls} /></div>
                <div><label className="text-sm font-medium text-neutral-700">Monthly membership (USD)</label>
                  <input data-testid={`plan-monthly-${p.id}`} type="number" step="0.01" value={p.monthly_price ?? 9.99} onChange={(e) => setPlan(i, "monthly_price", parseFloat(e.target.value) || 0)} className={inputCls} /></div>
                <div><label className="text-sm font-medium text-neutral-700">Description</label>
                  <input value={p.description} onChange={(e) => setPlan(i, "description", e.target.value)} className={inputCls} /></div>
              </div>
              <div className="mt-4">
                <label className="text-sm font-medium text-neutral-700">Features (one per line — shown on the pricing cards)</label>
                <textarea data-testid={`plan-features-${p.id}`} rows={4} value={(p.features || []).join("\n")}
                  onChange={(e) => setPlan(i, "features", e.target.value.split("\n"))}
                  className={inputCls} placeholder={"Manuscript review\nInterior formatting\nPublishing upload & setup"} />
              </div>
              <div className="mt-4">
                <label className="text-sm font-medium text-neutral-700">Platform modules unlocked by this tier</label>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
                  {Object.entries(MODULE_LABELS).map(([key, label]) => (
                    <label key={key} className="flex items-center gap-2 text-sm text-neutral-700 border border-neutral-200 rounded-md px-3 py-2 cursor-pointer hover:bg-neutral-50">
                      <input type="checkbox" data-testid={`plan-module-${p.id}-${key}`}
                        checked={(p.modules || []).includes(key)}
                        onChange={(e) => setPlan(i, "modules", e.target.checked
                          ? [...(p.modules || []), key]
                          : (p.modules || []).filter((m) => m !== key))} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          ))}
          <button data-testid="save-pricing-button"
            onClick={() => save({ pricing_plans: settings.pricing_plans.map((p) => ({ ...p, features: (p.features || []).map((f) => f.trim()).filter(Boolean) })) })}
            className="px-5 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">Save pricing</button>
        </div>
      )}

      {tab === "splits" && (
        <div className="space-y-4">
          {settings.royalty_split_templates.map((t, i) => (
            <div key={t.id} className="bg-white border border-neutral-200 rounded-lg p-6 grid sm:grid-cols-3 gap-4 items-end">
              <div><label className="text-sm font-medium text-neutral-700">Template name</label>
                <input value={t.name} onChange={(e) => setSplitT(i, "name", e.target.value)} className={inputCls} /></div>
              <div><label className="text-sm font-medium text-neutral-700">Author % of royalty</label>
                <input data-testid={`split-pct-${t.id}`} type="number" min="0" max="100" value={t.author_pct} onChange={(e) => setSplitT(i, "author_pct", parseInt(e.target.value) || 0)} className={inputCls} /></div>
              <label className="flex items-center gap-2 text-sm text-neutral-700 pb-2">
                <input type="radio" name="default-split" checked={settings.default_split_template === t.id}
                  onChange={() => setSettings((s) => ({ ...s, default_split_template: t.id }))} />
                Default for new submissions
              </label>
            </div>
          ))}
          <button data-testid="save-splits-button"
            onClick={() => save({ royalty_split_templates: settings.royalty_split_templates, default_split_template: settings.default_split_template })}
            className="px-5 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">Save splits</button>
        </div>
      )}

      {tab === "keys" && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6 space-y-4">
          <p className="text-sm text-neutral-500">Enter live credentials when ready. Stripe currently uses the built-in test key; a Resend key enables real email delivery.</p>
          <div className="bg-neutral-50 border border-neutral-200 rounded-md p-4 text-xs text-neutral-600 leading-relaxed">
            <p className="font-semibold text-neutral-800 mb-1">Going live with Stripe — checklist</p>
            1. In Stripe Dashboard → Product catalog, create a Product per plan with two Prices: a one-time price (bulk fee) and a recurring monthly price ($7.99–$12.99).<br/>
            2. Developers → Webhooks: add endpoint {"{your-domain}"}/api/webhook/stripe listening to checkout.session.completed, invoice.paid, customer.subscription.deleted — paste the signing secret (whsec_...) below.<br/>
            3. Settings → Billing → Customer portal: enable it so authors can update cards / cancel.<br/>
            4. Paste your pk_live / sk_live keys below. Until then, monthly membership renewals are collected as Stripe Checkout payments that extend access 30 days at a time.
          </div>
          {[["resend_api_key", "Resend API key (re_...)"], ["sender_email", "Sender email"],
            ["stripe_publishable_key", "Stripe publishable key (pk_live_...)"], ["stripe_secret_key", "Stripe secret key (sk_live_...)"],
            ["stripe_webhook_secret", "Stripe webhook secret (whsec_...)"],
            ["google_client_id", "Google OAuth client ID"], ["google_client_secret", "Google OAuth client secret"]].map(([k, label]) => (
            <div key={k}>
              <label className="text-sm font-medium text-neutral-700">{label}</label>
              <input data-testid={`apikey-${k}`} type={k.includes("secret") || k.includes("api_key") ? "password" : "text"}
                value={settings.api_keys?.[k] || ""} onChange={(e) => setKey(k, e.target.value)} className={inputCls} />
            </div>
          ))}
          <button data-testid="save-keys-button" onClick={() => save({ api_keys: settings.api_keys })}
            className="px-5 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">Save API keys</button>
        </div>
      )}
    </div>
  );
}
